import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLogoTheme } from '../context/LogoThemeContext';
import { fetchItemBySlug } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function AstrologiPage() {
  const { headerBgColor } = useLogoTheme();
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadData() {
      const data = await fetchItemBySlug('forlob_sider', 'forbundet-med-dit-horoskop');
      if (data) setPageData(data);
    }
    loadData();
  }, []);

  const heroBadge = pageData?.hero_badge || 'Astrologisk 1:1 session';
  const heroOverskrift = pageData?.hero_overskrift || 'FORBUNDET MED DIT HOROSKOP';
  const heroUndertitel = pageData?.hero_undertitel || '1:1 session – Dit fødselshoroskop som et kærligt spejl';
  const badge1 = pageData?.badge_linje_1 || 'Når det passer dig • 1 session • 75 min.';
  const badge2 = pageData?.badge_linje_2 || 'Opstartspris: 750 kr.';
  const prisTekst = pageData?.pris || '750 kr. (opstartspris)';
  const rammerOverskrift = pageData?.rammer_overskrift || 'Praktiske rammer for sessionen';
  const rammerPunkter = pageData?.rammer_punkter;
  const ctaTekst = pageData?.cta_tekst || 'Book en astrologisk 1:1 session';
  const billedeSrc = pageData?.billede?.[0]
    ? imageUrl(pageData.billede[0], 'width=1000,quality=85,format=auto')
    : 'https://assets.auxodev.dk/assets/40e16184-d312-45e0-99c1-a83e20a53a01/stjernehimmel-nz_d905e594.jpeg';

  return (
    <>
      <SEO
        title="Forbundet med dit horoskop 1:1 session - Lenea Evolinee"
        description="Dit fødselshoroskop fortæller ikke, hvem du skal være. Det kan i stedet være et spejl, der hjælper dig med at få øje på dine mønstre, behov og styrker."
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: headerBgColor }} className="pt-36 pb-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
            {heroBadge}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal text-[#2E2A27]">
            {heroOverskrift}
          </h1>
          <p className="text-lg sm:text-xl text-[#5C524A] leading-relaxed max-w-2xl mx-auto font-sans font-medium">
            {heroUndertitel}
          </p>
          {(badge1 || badge2) && (
            <div className="pt-4 flex flex-wrap justify-center gap-3">
              {badge1 && (
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#FAF7F2] border border-[#D8CDBF] text-xs font-semibold text-[#2E2A27] tracking-wider uppercase">
                  {badge1}
                </span>
              )}
              {badge2 && (
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#F3E6E2] border border-[#D8ADA0]/40 text-xs font-semibold text-[#864B3D] tracking-wider uppercase">
                  {badge2}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Hovedindhold */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Tekstsektion */}
            <div className="lg:col-span-7">
              {pageData?.indhold_html ? (
                <div
                  className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
                  dangerouslySetInnerHTML={{ __html: pageData.indhold_html }}
                />
              ) : (
                <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                  <p className="font-serif text-2xl text-[#2E2A27] font-normal">
                    Forbundet med dit horoskop 1:1 session
                  </p>
                  <p>
                    Dit fødselshoroskop fortæller ikke, hvem du skal være. Det kan i stedet være et spejl, der hjælper dig med at få øje på nogle af de mønstre, behov, styrker og temaer, som allerede findes i dig.
                  </p>
                  <p>
                    I Forbundet med dit horoskop - tager vi udgangspunkt i dit personlige fødselshoroskop og i det, der fylder hos dig lige nu. Måske er der noget, du genkender, noget du undrer dig over, eller noget i dig, du længes efter at forstå lidt bedre.
                  </p>
                  <p>
                    Du behøver ikke vide noget om astrologi på forhånd, alle er velkommen.
                  </p>
                  <p>
                    Inden sessionen sender du mig de spørgsmål eller temaer, du gerne vil have, at vi ser nærmere på. Jeg holder rummet og guider dig gennem horoskopet på en enkel og nærværende måde, og vi tager os tid til at være nysgerrige på det, der viser sig undervejs.
                  </p>
                  <p>
                    For mig handler astrologi ikke om at give dig færdige svar eller fortælle dig, hvad du skal gøre. Det handler om at skabe en større forståelse af dig selv og måske åbne for noget, du allerede mærker, ved eller længes efter at forstå.
                  </p>
                  <p>
                    Vi kan blandt andet se på dine potentialer og personlighedstræk, dine mønstre i relationer, det der giver dig energi og mening, eller temaer og forandringer, du står midt i.
                  </p>
                  <p>
                    Måske er der noget, du længes efter at forstå eller komme tættere på.
                  </p>
                  <p className="font-medium text-[#2E2A27]">
                    Vi følger det, der føles relevant for dig.
                  </p>
                  <p>
                    Du behøver ikke forberede noget særligt. Det vigtigste er blot, at du kommer med det, der fylder hos dig, og det du er nysgerrig på.
                  </p>
                  <p className="italic text-[#98866C]">
                    Astrologien kan ikke fortælle dig, hvad du skal gøre. Men den kan måske åbne et nyt blik eller bevidsthed på det, du allerede mærker, ved eller længes efter at forstå.
                  </p>
                  <blockquote className="my-6 p-6 sm:p-8 bg-[#FAF7F2] rounded-sm border-l-4 border-l-[#98866C] border-y border-r border-[#E8DFD5] space-y-2 text-[#2E2A27] italic">
                    <p className="font-serif text-lg text-[#2E2A27]">
                      “Kun en tåbe styres af stjernerne, den vise styrer dem selv.”
                    </p>
                    <p className="text-xs uppercase tracking-widest text-[#98866C] not-italic font-semibold pt-1">
                      – gammelt astrologisk mundheld
                    </p>
                  </blockquote>
                  <p className="font-semibold text-[#864B3D] text-sm sm:text-base bg-[#F3E6E2]/40 p-4 rounded-sm border border-[#D8ADA0]/30">
                    Jeg er i gang med min uddannelse i diplomastrologi og er færdig i starten af sommeren 2027. Derefter stiger mine priser for - Forbundet med dit horoskop.
                  </p>
                </div>
              )}
            </div>

            {/* Billede */}
            <div className="lg:col-span-5">
              <div className="rounded-arch overflow-hidden border border-[#D8CDBF] shadow-lg aspect-4/5 max-w-sm mx-auto lg:max-w-none bg-[#EAE3D5]">
                <img
                  src={billedeSrc}
                  alt="Forbundet med dit horoskop"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Praktiske Rammer */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center space-y-4">
            <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
              Sessionens opbygning
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {rammerOverskrift}
            </h2>
          </div>

          <div className="bg-white p-8 sm:p-12 rounded-sm border border-[#D8CDBF] shadow-xs space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="font-serif text-xl font-normal text-[#2E2A27]">
                  Hvad du kan forvente
                </h4>
                <ul className="space-y-3 text-sm sm:text-base text-[#5C524A]">
                  {rammerPunkter ? (
                    rammerPunkter.split('•').map((punkt, idx) => {
                      const trimmed = punkt.trim();
                      if (!trimmed) return null;
                      return (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="fa-solid fa-sparkles text-[#98866C] mt-1 text-xs shrink-0" aria-hidden="true" />
                          <span>{trimmed}</span>
                        </li>
                      );
                    })
                  ) : (
                    <>
                      <li className="flex items-start gap-3">
                        <i className="fa-solid fa-sparkles text-[#98866C] mt-1 text-xs shrink-0" aria-hidden="true" />
                        <span>1 session af ca. 75 minutters varighed</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="fa-solid fa-sparkles text-[#98866C] mt-1 text-xs shrink-0" aria-hidden="true" />
                        <span>Tager udgangspunkt i dit personlige fødselshoroskop</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="fa-solid fa-sparkles text-[#98866C] mt-1 text-xs shrink-0" aria-hidden="true" />
                        <span>Fokus på det, der fylder hos dig lige nu</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <i className="fa-solid fa-sparkles text-[#98866C] mt-1 text-xs shrink-0" aria-hidden="true" />
                        <span>Mulighed for session i klinikken i Farum eller online</span>
                      </li>
                    </>
                  )}
                </ul>
              </div>

              <div className="space-y-4 md:border-l md:border-[#E8DFD5] md:pl-8 flex flex-col justify-between">
                <div>
                  <h4 className="font-serif text-xl font-normal text-[#2E2A27] mb-2">
                    Pris & Investering
                  </h4>
                  <div className="space-y-1">
                    <p className="font-serif text-3xl text-[#98866C] font-semibold">
                      {prisTekst}
                    </p>
                    <p className="text-xs uppercase tracking-widest text-[#5C524A]">
                      For 75 minutters 1:1 fordybelse
                    </p>
                  </div>
                </div>

                <div className="pt-6">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center justify-center gap-2 w-full px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer shadow-xs"
                  >
                    <span>{ctaTekst}</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bund CTA */}
      <section className="py-20 bg-white text-center border-t border-[#E8DFD5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
            Er du nysgerrig?
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
            Få et nyt blik på dine mønstre og styrker
          </h2>
          <p className="text-base text-[#5C524A] font-sans">
            Du behøver ikke vide noget om astrologi på forhånd. Kom blot med det, der fylder hos dig, og det du er nysgerrig på.
          </p>
          <div>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer"
            >
              <span>Book din session her</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
